// Which input the person last used, written to <html data-input="pointer|keyboard">. index.css
// reads it so a click never draws a focus ring while Tab still does.
// docs/operations/design-standard.md, "Controls".

export type InputModality = 'pointer' | 'keyboard';

const MOVES = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown']);
const NON_TEXT_INPUT = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file', 'color', 'image']);

/** A target where arrows, Home and End move a caret rather than focus. */
const takesText = (el: EventTarget | null): boolean => {
  const node = el as HTMLElement | null;
  if (!node || typeof node.tagName !== 'string') return false;
  if (node.isContentEditable) return true;
  const tag = node.tagName;
  if (tag === 'TEXTAREA') return true;
  return tag === 'INPUT' && !NON_TEXT_INPUT.has((node as HTMLInputElement).type);
};

/** Keys that mean "I am moving focus with the keyboard"; typing into a field is not one. */
export const isNavigationKey = (e: Pick<KeyboardEvent, 'key' | 'target'>): boolean =>
  e.key === 'Tab' || (MOVES.has(e.key) && !takesText(e.target));

/** Starts tracking. Pointer until a navigation key is pressed. Returns the undo. */
export function installInputModality(doc: Document = document): () => void {
  const root = doc.documentElement;
  const set = (m: InputModality) => { if (root.dataset.input !== m) root.dataset.input = m; };
  const onPointer = () => set('pointer');
  const onKey = (e: KeyboardEvent) => { if (isNavigationKey(e)) set('keyboard'); };
  set('pointer');
  doc.addEventListener('pointerdown', onPointer, true);
  doc.addEventListener('keydown', onKey, true);
  return () => {
    doc.removeEventListener('pointerdown', onPointer, true);
    doc.removeEventListener('keydown', onKey, true);
    delete root.dataset.input;
  };
}
